import { useCallback, memo, useMemo } from 'react';
import { round, map } from 'lodash';
import { Formik } from 'formik';
import { useDispatch, useSelector } from '@client/hooks/redux.hooks';
import { getProductsForShow } from '@client/selectors/entities/shows';
import { startCheckoutFreeTickets, startCheckoutSession, startReserveTickets } from '@client/slices/features/stripe.reducer';
import { getSessionUserFullName, getSessionUserState } from '@client/selectors/helpers/session';
import { openAddNameModal } from './AddName';
import { PaidTicketModalGuestSchema, PaidTicketModalSchema } from './ticket-modal.schema';
import { hasPaidProductSelected } from './common/utils';
import { TicketModalForm } from './TicketModalForm';
import type { TicketFormPayload } from './interfaces';
import type { ObjectSchema } from 'yup';

export const TicketModal = memo(() => {
  const dispatch = useDispatch();
  const products = useSelector(getProductsForShow);

  const formData = useMemo(() => map(products, (product) => ({
    id: product.id,
    ticketPrice: '',
    minimumPrice: round(product.minimumPrice / 100, 2),
    hasVariablePrice: product.hasVariablePrice,
    quantity: products.length > 1 ? 0 : 1,
  })), [products]);

  const initialValues: TicketFormPayload = {
    prices: formData,
    shareContact: false,
    acceptsTos: false,
  };

  const userState = useSelector(getSessionUserState);
  const name = useSelector(getSessionUserFullName);

  const handleSubmit = useCallback((values: typeof initialValues) => {
    const isFreePurchase = !hasPaidProductSelected(values.prices);

    if (userState) {
      if (isFreePurchase) {
        const callback = () => dispatch(startCheckoutFreeTickets({
          shareContact: values.shareContact,
        }));

        if (name) {
          callback();
        } else {
          openAddNameModal({
            onSuccess: callback,
            successScope: 'start-checkout-session',
          });
        }
      } else {
        dispatch(startCheckoutSession({ shareContact: values.shareContact }));
      }
    } else {
      dispatch(startReserveTickets({ shareContact: values.shareContact }));
    }
  }, [dispatch, userState, name]);

  let schema: ObjectSchema<any> = PaidTicketModalSchema;

  if (!userState) {
    schema = PaidTicketModalGuestSchema;
  }

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={handleSubmit}
      validationSchema={schema}
      enableReinitialize
    >
      {(formProps) => (
        <TicketModalForm {...formProps} />
      )}
    </Formik>
  );
});

export default TicketModal;
